import type { SupportedLocale } from '@fleetcore/shared'

import { api } from '../../lib/api-client'
import { API_PREFIX } from '../../lib/endpoints'
import type { ChannelKey } from '../vehicles/api-types'
import type { FleetCapabilityView, TelemetrySeriesView, VehicleDtcView } from './api-types'

/**
 * The diagnostics module's paths.
 *
 * `/vehicles/:id/telemetry` already exists from Phase 2 and is reused verbatim for the
 * battery-voltage trend: a second endpoint returning the same rows in a different
 * shape is a second place for the hypertable's semantics to be misread. The other two
 * are Phase 4 and are documented in NOTES-PHASE4-WEB.md §2 as the contract the API
 * must answer, since the two halves were written in parallel.
 */
export const diagnosticsEndpoints = {
  vehicleDtc: (id: string) => `${API_PREFIX}/vehicles/${id}/dtc`,
  vehicleSeries: (id: string) => `${API_PREFIX}/vehicles/${id}/telemetry`,
  fleetCapabilities: `${API_PREFIX}/diagnostics/capabilities`,
} as const

/**
 * The locale travels on the query string for the same reason it does on the raw-data
 * panel: DTC descriptions are resolved server-side from the shared dictionary, which
 * the catalog deliberately does not duplicate, so a language switch has to reach them.
 */
export function fetchVehicleDtc(id: string, locale: SupportedLocale): Promise<VehicleDtcView> {
  return api.get<VehicleDtcView>(`${diagnosticsEndpoints.vehicleDtc(id)}?locale=${locale}`)
}

export function fetchFleetCapabilities(locale: SupportedLocale): Promise<FleetCapabilityView> {
  return api.get<FleetCapabilityView>(`${diagnosticsEndpoints.fleetCapabilities}?locale=${locale}`)
}

export interface SeriesParams {
  channel: ChannelKey
  from?: string
  to?: string
  limit?: number
}

export function fetchTelemetrySeries(
  id: string,
  params: SeriesParams,
  locale: SupportedLocale,
): Promise<TelemetrySeriesView> {
  const search = new URLSearchParams({ channel: params.channel, locale })
  if (params.from !== undefined) search.set('from', params.from)
  if (params.to !== undefined) search.set('to', params.to)
  if (params.limit !== undefined) search.set('limit', String(params.limit))
  return api.get<TelemetrySeriesView>(
    `${diagnosticsEndpoints.vehicleSeries(id)}?${search.toString()}`,
  )
}
